import React, { useState, useEffect } from 'react';
import {
    DmsForm,
    DmsTextField,
    DmsLoading,
    useGet,
    intl
} from 'dmsbasereact';
import { Box, Typography } from '@mui/material';
import { SettingsSection, SettingsField } from '../../components/SettingsLayouts';

/**
 * SAPIntegrationSettings - SAP API configuration form.
 * Communication logs are available in a separate page under System > SAP Communication Logs.
 */
export const SAPIntegrationSettings = () => {
    const sapConfigurationURL = '/sap-configuration';
    const { data, isLoading, error } = useGet(sapConfigurationURL, null, true);
    const [sapConfigurationData, setSapConfigurationData] = useState(null);

    useEffect(() => {
        if (data?.data) {
            setSapConfigurationData(data.data);
        }
    }, [data]);

    const handleChange = (name, value) => {
        setSapConfigurationData((prevData) => ({
            ...(prevData || {}),
            [name]: value,
        }));
    };

    if (isLoading) {
        return <DmsLoading />;
    }

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">
                    {intl.get("ErrorLoadingConfiguration").d("Error loading SAP configuration")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {intl.get("PleaseEnsureBackendIsRunning").d("Please ensure the backend API is running and the endpoint /sap-configuration exists")}
                </Typography>
            </Box>
        );
    }

    if (!sapConfigurationData) {
        return <DmsLoading />;
    }

    return (
        <DmsForm
            url={sapConfigurationURL}
            method="PUT"
            fieldTypes={{
                token_expiration_time: "float",
                token_failure_retry_time: "float",
                pending_time_validation: "float",
            }}
        >
            <DmsForm.Body>
                <SettingsSection
                    title={intl.get('SAPAPIConfiguration').d('SAP API Configuration')}
                    description={intl.get('SAPAPIConfigurationDescription').d(
                        'Configure connection parameters for SAP integration.'
                    )}
                    showSwitch={false}
                >
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="base_url"
                            label={intl.get("BaseURL").d("Base URL")}
                            fullWidth
                            value={sapConfigurationData.base_url ?? ""}
                            onChange={(e) => handleChange("base_url", e.target.value)}
                            helperText={intl.get("SAPBaseURLHelp").d("SAP API base URL")}
                        />
                    </SettingsField>
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="username"
                            label={intl.get("Username").d("Username")}
                            fullWidth
                            value={sapConfigurationData.username ?? ""}
                            onChange={(e) => handleChange("username", e.target.value)}
                            helperText={intl.get("SAPUsernameHelp").d("SAP authentication username")}
                        />
                    </SettingsField>
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="password"
                            type="password"
                            label={intl.get("Password").d("Password")}
                            fullWidth
                            helperText={intl.get("LeaveBlankToKeep").d("Leave blank to keep existing")}
                        />
                    </SettingsField>
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="token_endpoint"
                            label={intl.get("TokenEndpoint").d("Token Endpoint")}
                            fullWidth
                            value={sapConfigurationData.token_endpoint ?? ""}
                            onChange={(e) => handleChange("token_endpoint", e.target.value)}
                            helperText={intl.get("SAPTokenEndpointHelp").d("Endpoint to request authentication token")}
                        />
                    </SettingsField>
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="token_expiration_time"
                            label={intl.get("TokenExpirationTime").d("Token Expiration Time (seconds)")}
                            fullWidth
                            type="number"
                            value={sapConfigurationData.token_expiration_time ?? ""}
                            onChange={(e) => handleChange("token_expiration_time", e.target.value)}
                            helperText={intl.get("SAPTokenExpirationHelp").d("Time in seconds before token expires")}
                        />
                    </SettingsField>
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="token_failure_retry_time"
                            label={intl.get("TokenFailureRetryTime").d("Token Failure Retry Time (seconds)")}
                            fullWidth
                            type="number"
                            value={sapConfigurationData.token_failure_retry_time ?? ""}
                            onChange={(e) => handleChange("token_failure_retry_time", e.target.value)}
                            helperText={intl.get("SAPTokenRetryHelp").d("Wait time after token failure")}
                        />
                    </SettingsField>
                    <SettingsField sm={6} md={4}>
                        <DmsTextField
                            name="pending_time_validation"
                            label={intl.get("PendingTimeValidation").d("Pending Time Validation (seconds)")}
                            fullWidth
                            type="number"
                            value={sapConfigurationData.pending_time_validation ?? ""}
                            onChange={(e) => handleChange("pending_time_validation", e.target.value)}
                            helperText={intl.get("SAPPendingTimeHelp").d("Time between pending request checks")}
                        />
                    </SettingsField>
                </SettingsSection>
            </DmsForm.Body>
        </DmsForm>
    );
};
